import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Flex,
  IconButton,
  Tooltip,
  Image,
  Input,
  FormControl,
  FormLabel,
  VStack,
  Select,
  SimpleGrid,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Spacer,
  useToast,
  TabPanel,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import UuidPreview from "./UuidPreview";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlineSave,
  HiOutlinePlus,
  HiOutlineTrash,
} from "react-icons/hi";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import ClockSelect from "./ClockSelect";
import CodesetSelect from "./CodesetSelect";
import PartySelect from "./PartySelect";
import PartySelectById from "./PartySelectById";

const ProductDefinitionEditor = ({
  product,
  isNew: initialIsNew,
  onSave,
  onBack,
}) => {
  const cardBg = useColorModeValue("white", "gray.700");
  const [localProduct, setLocalProduct] = useState(product || {});
  const [isNew, setIsNew] = useState(initialIsNew ?? false);
  const toast = useToast();
  const navigate = useNavigate();
  const [registers, setRegisters] = useState([]);
  const [registerLoading, setRegisterLoading] = useState(true);
  useEffect(() => {
    const fetchRegisters = async () => {
      try {
        const res = await fetch("https://registers.poc.mrc.ai/api/registers");
        if (!res.ok) throw new Error("Failed to fetch registers");
        const data = await res.json();
        setRegisters(data);
      } catch (err) {
        console.error("❌ Error fetching registers", err);
      } finally {
        setRegisterLoading(false);
      }
    };

    fetchRegisters();
  }, []);
  const handleChange = (field, value) => {
    setLocalProduct((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      const url = isNew
        ? `https://poc.mrc.ai/${localProduct.code}/api/product`
        : `https://poc.mrc.ai/${localProduct.code}/api/product/${localProduct._id}`;

      const method = isNew ? "POST" : "PUT";
      console.log(
        `Saving product to ${url} with method ${method}`,
        localProduct
      );

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(localProduct),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to ${isNew ? "create" : "update"} product. Status: ${
            response.status
          }`
        );
      }

      const result = await response.json();

      toast({
        title: isNew ? "Product created" : "Product saved",
        description: isNew
          ? "New product was added."
          : "Changes saved successfully.",
        status: "success",
        duration: 3000,
      });

      if (isNew && result.code) {
        setIsNew(false);
        setLocalProduct(result);
        if (onSave) onSave(result, true);
        navigate(`/poc/product/${result.code}/edit`, {
          state: { isNew: false },
        });
      } else {
        if (onSave) onSave(result, false);
      }
    } catch (error) {
      console.error("❌ Error saving product:", error);

      toast({
        title: "Save failed",
        description:
          error.message || "An error occurred while saving the product.",
        status: "error",
        duration: 4000,
      });
    }
  };

  const handleBack = () => {
    if (onBack) onBack();
    else navigate("/poc", { replace: true });
  };

  const updateOracleField = (
    apdIdx,
    stageIdx,
    activityIdx,
    oracleIdx,
    field,
    value
  ) => {
    const updated = [...(localProduct.activityPatternDefinitions || [])];
    const oracleRef =
      updated?.[apdIdx]?.stages?.[stageIdx]?.activities?.[activityIdx]
        ?.oracles?.[oracleIdx];
    if (!oracleRef) return;

    if (field === "party") {
      // value should be a string ID or null
      oracleRef.party = value ?? null;
    } else {
      oracleRef[field] = value ?? null;
    }

    handleChange("activityPatternDefinitions", updated);
  };

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />
        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Flex mb={2}>
              <HStack spacing={6}>
                <Tooltip label="Back to the Environment Homepage">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleBack}
                  />
                </Tooltip>
                <Heading size="lg">
                  {localProduct.name} Product Definition
                </Heading>
                <FormLabel>({localProduct.code})</FormLabel>
                <Tooltip label="Save Product Definition">
                  <IconButton
                    icon={<HiOutlineSave />}
                    size="md"
                    onClick={handleSave}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>

          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <UuidPreview size="xs" uuid={localProduct._id} />

            <FormControl>
              <FormLabel fontSize="xs">Description</FormLabel>
              <Input
                size="xs"
                value={localProduct.description}
                onChange={(e) => handleChange("description", e.target.value)}
              />
            </FormControl>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <ClockSelect
                label="Clock"
                value={localProduct.clockId} // <-- this matches a clock._id
                onChange={(clockId) => handleChange("clockId", clockId)} // <-- stores the selected _id in product.clockId
                size="xs"
                height="40px"
              />

              <FormControl>
                <FormLabel fontSize="xs">Data Standard Version</FormLabel>
                <Input
                  size="xs"
                  value={localProduct.dataStandardVersionCode}
                  onChange={(e) =>
                    handleChange("dataStandardVersionCode", e.target.value)
                  }
                />
              </FormControl>
            </SimpleGrid>
          </Box>
          <Box
            borderWidth="1px"
            borderRadius="xs"
            p={4}
            borderColor={"gray.500"}
            backgroundColor="whiteAlpha.800">
            <Heading size="md" mb={4}>
              Definitions
            </Heading>
            <Tabs
              orientation="horizontal"
              variant="line"
              colorScheme="linkedin">
              <TabList>
                <Tab>Endpoints</Tab>
                <Tab>Signals Register</Tab>
                <Tab>Triggers Register</Tab>
                <Tab>Events Register</Tab>
                <Tab>Product Register</Tab>
                <Tab>Product Templates</Tab>
                <Tab>Workflow Register</Tab>
                <Tab>Activity Patterns</Tab>
                <Tab>Controls</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <SimpleGrid columns={{ base: 1, md: 7 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="xs">Signals</FormLabel>
                      <Input
                        size="xs"
                        value={localProduct.routing?.signalsEndpoint}
                        onChange={(e) =>
                          handleChange(
                            "routing.signalsEndpoint",
                            e.target.value
                          )
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">Triggers</FormLabel>
                      <Input
                        size="xs"
                        value={localProduct.routing?.triggersEndpoint}
                        onChange={(e) =>
                          handleChange(
                            "routing.triggersEndpoint",
                            e.target.value
                          )
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">Events</FormLabel>
                      <Input
                        size="xs"
                        value={localProduct.routing?.eventsEndpoint}
                        onChange={(e) =>
                          handleChange("routing.eventsEndpoint", e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">Product</FormLabel>
                      <Input
                        size="xs"
                        value={localProduct.routing?.productEndpoint}
                        onChange={(e) =>
                          handleChange(
                            "routing.productEndpoint",
                            e.target.value
                          )
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">Contracts</FormLabel>
                      <Input
                        size="xs"
                        value={localProduct.routing?.contractsEndpoint}
                        onChange={(e) =>
                          handleChange(
                            "routing.contractsEndpoint",
                            e.target.value
                          )
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">Workflow</FormLabel>
                      <Input
                        size="xs"
                        value={localProduct.routing?.workflowEndpoint}
                        onChange={(e) =>
                          handleChange(
                            "routing.workflowEndpoint",
                            e.target.value
                          )
                        }
                      />
                    </FormControl>
                  </SimpleGrid>
                </TabPanel>
                <TabPanel>
                  <HStack spacing={4} mb={2}>
                    <Heading size="sm" mb={2}>
                      Signals Register
                    </Heading>
                    <Tooltip
                      label="Add Signals Register entry..."
                      aria-label="Add Signals Register entry tooltip">
                      <IconButton
                        icon={<HiOutlinePlus />}
                        size="sm"
                        onClick={() => {
                          const updated = [
                            ...(localProduct.signalRegister || []),
                          ];
                          updated.push({
                            type: "",
                            route: "",
                            objectType: "",
                            objectId: null,
                          });
                          handleChange("signalRegister", updated);
                        }}
                      />
                    </Tooltip>
                  </HStack>

                  <Table
                    variant="unstyled"
                    size="sm"
                    fontSize="xs"
                    style={{ tableLayout: "auto", width: "100%" }}
                    sx={{
                      "tbody tr:nth-of-type(odd)": {
                        backgroundColor: "gray.200",
                      },
                    }}>
                    <Thead>
                      <Tr>
                        <Th fontSize="xs">Name</Th>
                        <Th fontSize="xs">Type</Th>
                        <Th fontSize="xs">Route</Th>

                        <Th fontSize="xs">Object Type</Th>
                        <Th fontSize="xs">Standard Payload</Th>
                        <Th fontSize="xs"></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {localProduct.signalRegister?.map((signal, idx) => (
                        <Tr key={signal.type || idx}>
                          <Td>
                            <Input
                              size="xs"
                              value={signal.name || ""}
                              onChange={(e) => {
                                const updated = [
                                  ...localProduct.signalRegister,
                                ];
                                updated[idx].name = e.target.value;
                                handleChange("signalRegister", updated);
                              }}
                            />
                          </Td>
                          <Td>
                            <Input
                              size="xs"
                              value={signal.type || ""}
                              onChange={(e) => {
                                const updated = [
                                  ...localProduct.signalRegister,
                                ];
                                updated[idx].type = e.target.value;
                                handleChange("signalRegister", updated);
                              }}
                            />
                          </Td>
                          <Td>
                            <Input
                              size="xs"
                              value={signal.route || ""}
                              onChange={(e) => {
                                const updated = [
                                  ...localProduct.signalRegister,
                                ];
                                updated[idx].route = e.target.value;
                                handleChange("signalRegister", updated);
                              }}
                            />
                          </Td>

                          <Td>
                            <CodesetSelect
                              codesetCode="SLC009"
                              value={
                                signal.objectType
                                  ? {
                                      code: signal.objectType,
                                      description: "",
                                    }
                                  : null
                              }
                              onChange={(selected) => {
                                const updated = [
                                  ...localProduct.signalRegister,
                                ];
                                updated[idx].objectType = selected.code;
                                handleChange("signalRegister", updated);
                              }}
                              size="xs"
                            />
                          </Td>
                          <Td>
                            <CodesetSelect
                              codesetCode="B58"
                              value={
                                signal.standardPayloadType
                                  ? {
                                      code: signal.standardPayloadType,
                                      description: "",
                                    }
                                  : null
                              }
                              onChange={(selected) => {
                                const updated = [
                                  ...localProduct.signalRegister,
                                ];
                                updated[idx].standardPayloadType =
                                  selected.code;
                                handleChange("signalRegister", updated);
                              }}
                              size="xs"
                            />
                          </Td>
                          <Td>
                            <Tooltip label="Delete row">
                              <IconButton
                                icon={<HiOutlineTrash />}
                                size="xs"
                                onClick={() => {
                                  const updated = [
                                    ...localProduct.signalRegister,
                                  ];
                                  updated.splice(idx, 1);
                                  handleChange("signalRegister", updated);
                                }}
                              />
                            </Tooltip>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TabPanel>
                <TabPanel>
                  <HStack spacing={4} mb={2}>
                    <Heading size="sm" mb={2}>
                      Triggers Register
                    </Heading>
                    <Tooltip
                      label="Add Triggers Register entry..."
                      aria-label="Add Triggers Register entry tooltip">
                      <IconButton
                        icon={<HiOutlinePlus />}
                        size="sm"
                        onClick={() => {
                          const updated = [
                            ...(localProduct.triggerRegister || []),
                          ];
                          updated.push({
                            _id: crypto.randomUUID(),
                            type: "",
                            route: "",
                            objectType: "",
                            objectId: null,
                          });
                          handleChange("triggerRegister", updated);
                        }}
                      />
                    </Tooltip>
                  </HStack>

                  <Table
                    variant="unstyled"
                    size="sm"
                    fontSize="xs"
                    style={{ tableLayout: "auto", width: "100%" }}
                    sx={{
                      "tbody tr:nth-of-type(odd)": {
                        backgroundColor: "gray.200",
                      },
                    }}>
                    <Thead>
                      <Tr>
                        <Th fontSize="xs">Name</Th>
                        <Th fontSize="xs">Type</Th>
                        <Th fontSize="xs">Route</Th>
                        <Th fontSize="xs">Workflow</Th>
                        <Th fontSize="xs">Object Type</Th>
                        <Th fontSize="xs">Standard Payload</Th>
                        <Th fontSize="xs"></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {localProduct.triggerRegister?.map((trigger, idx) => (
                        <Tr key={trigger.type || idx}>
                          <Td>
                            <Input
                              size="xs"
                              value={trigger.name || ""}
                              onChange={(e) => {
                                const updated = [
                                  ...localProduct.triggerRegister,
                                ];
                                updated[idx].name = e.target.value;
                                handleChange("triggerRegister", updated);
                              }}
                            />
                          </Td>
                          <Td>
                            <Input
                              size="xs"
                              value={trigger.type || ""}
                              onChange={(e) => {
                                const updated = [
                                  ...localProduct.triggerRegister,
                                ];
                                updated[idx].type = e.target.value;
                                handleChange("triggerRegister", updated);
                              }}
                            />
                          </Td>
                          <Td>
                            <Input
                              size="xs"
                              value={trigger.route || ""}
                              onChange={(e) => {
                                const updated = [
                                  ...localProduct.triggerRegister,
                                ];
                                updated[idx].route = e.target.value;
                                handleChange("triggerRegister", updated);
                              }}
                            />
                          </Td>

                          <Td>
                            <Input
                              size="xs"
                              value={trigger.workflow || ""}
                              onChange={(e) => {
                                const updated = [
                                  ...localProduct.triggerRegister,
                                ];
                                updated[idx].workflow = e.target.value;
                                handleChange("triggerRegister", updated);
                              }}
                            />
                          </Td>

                          <Td>
                            <CodesetSelect
                              codesetCode="SLC009"
                              value={
                                trigger.objectType
                                  ? {
                                      code: trigger.objectType,
                                      description: "",
                                    }
                                  : null
                              }
                              onChange={(selected) => {
                                const updated = [
                                  ...localProduct.triggerRegister,
                                ];
                                updated[idx].objectType = selected.code;
                                handleChange("triggerRegister", updated);
                              }}
                              size="xs"
                            />
                          </Td>
                          <Td>
                            <CodesetSelect
                              codesetCode="B58"
                              value={
                                trigger.standardPayloadType
                                  ? {
                                      code: trigger.standardPayloadType,
                                      description: "",
                                    }
                                  : null
                              }
                              onChange={(selected) => {
                                const updated = [
                                  ...localProduct.triggerRegister,
                                ];
                                updated[idx].standardPayloadType =
                                  selected.code;
                                handleChange("triggerRegister", updated);
                              }}
                              size="xs"
                            />
                          </Td>
                          <Td>
                            <Tooltip label="Delete row">
                              <IconButton
                                icon={<HiOutlineTrash />}
                                size="xs"
                                onClick={() => {
                                  const updated = [
                                    ...localProduct.triggerRegister,
                                  ];
                                  updated.splice(idx, 1);
                                  handleChange("triggerRegister", updated);
                                }}
                              />
                            </Tooltip>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TabPanel>
                <TabPanel>
                  <HStack spacing={4} mb={2}>
                    <Heading size="sm" mb={2}>
                      Events Register
                    </Heading>
                    <Tooltip
                      label="Add Events Register entry..."
                      aria-label="Add Events Register entry tooltip">
                      <IconButton
                        icon={<HiOutlinePlus />}
                        size="sm"
                        onClick={() => {
                          const updated = [
                            ...(localProduct.eventRegister || []),
                          ];
                          updated.push({
                            type: "",
                            route: "",
                            objectType: "",
                            objectId: null,
                          });
                          handleChange("eventRegister", updated);
                        }}
                      />
                    </Tooltip>
                  </HStack>

                  <Table
                    variant="unstyled"
                    size="sm"
                    fontSize="xs"
                    style={{ tableLayout: "auto", width: "100%" }}
                    sx={{
                      "tbody tr:nth-of-type(odd)": {
                        backgroundColor: "gray.200",
                      },
                    }}>
                    <Thead>
                      <Tr>
                        <Th fontSize="xs">Name</Th>
                        <Th fontSize="xs">Type</Th>
                        <Th fontSize="xs">Route</Th>

                        <Th fontSize="xs">Object Type</Th>
                        <Th fontSize="xs">Standard Payload</Th>
                        <Th fontSize="xs"></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {localProduct.eventRegister?.map((event, idx) => (
                        <Tr key={event.type || idx}>
                          <Td>
                            <Input
                              size="xs"
                              value={event.name || ""}
                              onChange={(e) => {
                                const updated = [
                                  ...localProduct.eventRegister,
                                ];
                                updated[idx].name = e.target.value;
                                handleChange("eventRegister", updated);
                              }}
                            />
                          </Td>
                          <Td>
                            <Input
                              size="xs"
                              value={event.type || ""}
                              onChange={(e) => {
                                const updated = [...localProduct.eventRegister];
                                updated[idx].type = e.target.value;
                                handleChange("eventRegister", updated);
                              }}
                            />
                          </Td>
                          <Td>
                            <Input
                              size="xs"
                              value={event.route || ""}
                              onChange={(e) => {
                                const updated = [...localProduct.eventRegister];
                                updated[idx].route = e.target.value;
                                handleChange("eventRegister", updated);
                              }}
                            />
                          </Td>

                          <Td>
                            <CodesetSelect
                              codesetCode="SLC009"
                              value={
                                event.objectType
                                  ? {
                                      code: event.objectType,
                                      description: "",
                                    }
                                  : null
                              }
                              onChange={(selected) => {
                                const updated = [...localProduct.eventRegister];
                                updated[idx].objectType = selected.code;
                                handleChange("eventRegister", updated);
                              }}
                              size="xs"
                            />
                          </Td>
                          <Td>
                            <CodesetSelect
                              codesetCode="B58"
                              value={
                                event.standardPayloadType
                                  ? {
                                      code: event.standardPayloadType,
                                      description: "",
                                    }
                                  : null
                              }
                              onChange={(selected) => {
                                const updated = [...localProduct.eventRegister];
                                updated[idx].standardPayloadType =
                                  selected.code;
                                handleChange("eventRegister", updated);
                              }}
                              size="xs"
                            />
                          </Td>
                          <Td>
                            <Tooltip label="Delete row">
                              <IconButton
                                icon={<HiOutlineTrash />}
                                size="xs"
                                onClick={() => {
                                  const updated = [
                                    ...localProduct.eventRegister,
                                  ];
                                  updated.splice(idx, 1);
                                  handleChange("eventRegister", updated);
                                }}
                              />
                            </Tooltip>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TabPanel>

                <TabPanel>
                  <HStack spacing={4} mb={2}>
                    <Heading size="sm" mb={2}>
                      Product Register
                    </Heading>
                    <Tooltip
                      label="Add Product Register entry..."
                      aria-label="Add Product Register entry tooltip">
                      <IconButton
                        icon={<HiOutlinePlus />}
                        size="sm"
                        onClick={() => {
                          const updated = [
                            ...(localProduct.productRegister || []),
                          ];
                          updated.push({
                            type: "",
                            route: "",
                            objectType: "",
                            objectId: null,
                          });
                          handleChange("productRegister", updated);
                        }}
                      />
                    </Tooltip>
                  </HStack>

                  <Table
                    variant="unstyled"
                    size="sm"
                    fontSize="xs"
                    style={{ tableLayout: "auto", width: "100%" }}
                    sx={{
                      "tbody tr:nth-of-type(odd)": {
                        backgroundColor: "gray.200",
                      },
                    }}>
                    <Thead>
                      <Tr>
                        <Th fontSize="xs">Name</Th>
                        <Th fontSize="xs">Type</Th>
                        <Th fontSize="xs">Route</Th>

                        <Th fontSize="xs">Object Type</Th>
                        <Th fontSize="xs">Standard Payload</Th>
                        <Th fontSize="xs"></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {localProduct.productRegister?.map((prodreg, idx) => (
                        <Tr key={prodreg.type || idx}>
                          <Td>
                            <Input
                              size="xs"
                              value={prodreg.name || ""}
                              onChange={(e) => {
                                const updated = [
                                  ...localProduct.productRegister,
                                ];
                                updated[idx].name = e.target.value;
                                handleChange("productRegister", updated);
                              }}
                            />
                          </Td>
                          <Td>
                            <Input
                              size="xs"
                              value={prodreg.type || ""}
                              onChange={(e) => {
                                const updated = [
                                  ...localProduct.productRegister,
                                ];
                                updated[idx].type = e.target.value;
                                handleChange("productRegister", updated);
                              }}
                            />
                          </Td>
                          <Td>
                            <Input
                              size="xs"
                              value={prodreg.route || ""}
                              onChange={(e) => {
                                const updated = [
                                  ...localProduct.productRegister,
                                ];
                                updated[idx].route = e.target.value;
                                handleChange("productRegister", updated);
                              }}
                            />
                          </Td>

                          <Td>
                            <CodesetSelect
                              codesetCode="SLC009"
                              value={
                                prodreg.objectType
                                  ? {
                                      code: prodreg.objectType,
                                      description: "",
                                    }
                                  : null
                              }
                              onChange={(selected) => {
                                const updated = [
                                  ...localProduct.productRegister,
                                ];
                                updated[idx].objectType = selected.code;
                                handleChange("productRegister", updated);
                              }}
                              size="xs"
                            />
                          </Td>
                          <Td>
                            <CodesetSelect
                              codesetCode="B58"
                              value={
                                prodreg.standardPayloadType
                                  ? {
                                      code: prodreg.standardPayloadType,
                                      description: "",
                                    }
                                  : null
                              }
                              onChange={(selected) => {
                                const updated = [
                                  ...localProduct.productRegister,
                                ];
                                updated[idx].standardPayloadType =
                                  selected.code;
                                handleChange("productRegister", updated);
                              }}
                              size="xs"
                            />
                          </Td>
                          <Td>
                            <Tooltip label="Delete row">
                              <IconButton
                                icon={<HiOutlineTrash />}
                                size="xs"
                                onClick={() => {
                                  const updated = [
                                    ...localProduct.productRegister,
                                  ];
                                  updated.splice(idx, 1);
                                  handleChange("productRegister", updated);
                                }}
                              />
                            </Tooltip>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TabPanel>










                 <TabPanel>
                  <HStack spacing={4} mb={2}>
                    <Heading size="sm" mb={2}>
                      Product Templates
                    </Heading>
                    <Tooltip
                      label="Add Product Template..."
                      aria-label="Add Product Template tooltip">
                      <IconButton
                        icon={<HiOutlinePlus />}
                        size="sm"

                      />
                    </Tooltip>
                  </HStack>

                  <Table
                    variant="unstyled"
                    size="sm"
                    fontSize="xs"
                    style={{ tableLayout: "auto", width: "100%" }}
                    sx={{
                      "tbody tr:nth-of-type(odd)": {
                        backgroundColor: "gray.200",
                      },
                    }}>
                    <Thead>
                      <Tr>
                        <Th fontSize="xs">Id</Th>
                        <Th fontSize="xs">Code</Th>
                        <Th fontSize="xs">Name</Th>
                        <Th fontSize="xs">Description</Th>
                        <Th fontSize="xs">Document Reference</Th>
                        <Th fontSize="xs"></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {localProduct.productTemplates?.map((prodtemp, idx) => (
                        <Tr key={prodtemp.id || idx}>
                          
                      
                     

                          <Td>
                            <Tooltip label="Delete row">
                              <IconButton
                                icon={<HiOutlineTrash />}
                                size="xs"
                                onClick={() => {
                                  const updated = [
                                    ...localProduct.productTemplates,
                                  ];
                                  updated.splice(idx, 1);
                                  handleChange("productTemplates", updated);
                                }}
                              />
                            </Tooltip>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TabPanel>























                <TabPanel>
                  <HStack spacing={4} mb={2}>
                    <Heading size="sm" mb={2}>
                      Workflow Register
                    </Heading>
                    <Tooltip
                      label="Add Workflow Register entry..."
                      aria-label="Add Workflow Register entry tooltip">
                      <IconButton
                        icon={<HiOutlinePlus />}
                        size="sm"
                        onClick={() => {
                          const updated = [
                            ...(localProduct.workflowRegister || []),
                          ];
                          updated.push({
                            type: "",
                            route: "",
                            objectType: "",
                            objectId: null,
                          });
                          handleChange("workflowRegister", updated);
                        }}
                      />
                    </Tooltip>
                  </HStack>

                  <Table
                    variant="unstyled"
                    size="sm"
                    fontSize="xs"
                    style={{ tableLayout: "auto", width: "100%" }}
                    sx={{
                      "tbody tr:nth-of-type(odd)": {
                        backgroundColor: "gray.200",
                      },
                    }}>
                    <Thead>
                      <Tr>
                        <Th fontSize="xs">Name</Th>
                        <Th fontSize="xs">Type</Th>
                        <Th fontSize="xs">Route</Th>
                        <Th fontSize="xs">Activity Pattern</Th>
                        <Th fontSize="xs">Object Type</Th>
                        <Th fontSize="xs">Standard Payload</Th>
                        <Th fontSize="xs"></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {localProduct.workflowRegister?.map((workflow, idx) => (
                        <Tr key={workflow.type || idx}>
                          <Td>
                            <Input
                              size="xs"
                              value={workflow.name || ""}
                              onChange={(e) => {
                                const updated = [
                                  ...localProduct.workflowRegister,
                                ];
                                updated[idx].name = e.target.value;
                                handleChange("workflowRegister", updated);
                              }}
                            />
                          </Td>
                          <Td>
                            <Input
                              size="xs"
                              value={workflow.type || ""}
                              onChange={(e) => {
                                const updated = [
                                  ...localProduct.workflowRegister,
                                ];
                                updated[idx].type = e.target.value;
                                handleChange("workflowRegister", updated);
                              }}
                            />
                          </Td>
                          <Td>
                            <Input
                              size="xs"
                              value={workflow.route || ""}
                              onChange={(e) => {
                                const updated = [
                                  ...localProduct.workflowRegister,
                                ];
                                updated[idx].route = e.target.value;
                                handleChange("workflowRegister", updated);
                              }}
                            />
                          </Td>

                          <Td>
                            <Select
                              placeholder="Select..."
                              size="xs"
                              value={String(workflow.activityPatternId)}
                              onChange={(e) => {
                                const updated = [
                                  ...localProduct.workflowRegister,
                                ];
                                updated[idx] = {
                                  ...updated[idx],
                                  activityPatternId: e.target.value,
                                };
                                handleChange("workflowRegister", updated);
                              }}>
                              {(
                                localProduct.activityPatternDefinitions || []
                              ).map((pattern) => {
                                const id = String(pattern._id);
                                return (
                                  <option
                                    key={pattern._id}
                                    value={pattern._id}
                                    disabled={!pattern._id}>
                                    {pattern.name}
                                  </option>
                                );
                              })}
                            </Select>
                          </Td>

                          <Td>
                            <CodesetSelect
                              codesetCode="SLC009"
                              value={
                                workflow.objectType
                                  ? {
                                      code: workflow.objectType,
                                      description: "",
                                    }
                                  : null
                              }
                              onChange={(selected) => {
                                const updated = [
                                  ...localProduct.workflowRegister,
                                ];
                                updated[idx].objectType = selected.code;
                                handleChange("workflowRegister", updated);
                              }}
                              size="xs"
                            />
                          </Td>
                          <Td>
                            <CodesetSelect
                              codesetCode="B58"
                              value={
                                workflow.standardPayloadType
                                  ? {
                                      code: workflow.standardPayloadType,
                                      description: "",
                                    }
                                  : null
                              }
                              onChange={(selected) => {
                                const updated = [
                                  ...localProduct.workflowRegister,
                                ];
                                updated[idx].standardPayloadType =
                                  selected.code;
                                handleChange("workflowRegister", updated);
                              }}
                              size="xs"
                            />
                          </Td>
                          <Td>
                            <Tooltip label="Delete row">
                              <IconButton
                                icon={<HiOutlineTrash />}
                                size="xs"
                                onClick={() => {
                                  const updated = [
                                    ...localProduct.workflowRegister,
                                  ];
                                  updated.splice(idx, 1);
                                  handleChange("workflowRegister", updated);
                                }}
                              />
                            </Tooltip>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TabPanel>

                <TabPanel>
                  <HStack spacing={2} mb={1}>
                    <Heading size="sm" mb={1}>
                      Activity Pattern Definitions
                    </Heading>
                    <Tooltip
                      label="Add Activity Pattern Definition..."
                      aria-label="Add Activity Pattern Definition tooltip">
                      <IconButton
                        icon={<HiOutlinePlus />}
                        size="sm"
                        onClick={() => {
                          const updated = [
                            ...(localProduct.activityPatternDefinitions || []),
                          ];
                          updated.push({
                            id: crypto.randomUUID(),
                            name: "New Activity Pattern Definition",
                            type: "",
                            objectId: null,
                            stages: [],
                          });
                          handleChange("activityPatternDefinitions", updated);
                        }}
                      />
                    </Tooltip>
                  </HStack>

                  {(localProduct.activityPatternDefinitions || []).map(
                    (apd, apdIdx) => (
                      <Box
                        key={apdIdx}
                        borderWidth="1px"
                        borderRadius="md"
                        p={1}
                        mb={1}
                        borderColor="gray.500">
                        <UuidPreview size="xs" uuid={apd.id} />

                        <SimpleGrid columns={{ base: 3, md: 6 }} spacing={4}>
                          <FormControl>
                            <FormLabel fontSize="xs">Name</FormLabel>
                            <Input
                              size="xs"
                              value={apd.name || ""}
                              onChange={(e) => {
                                const newActivityPatterns = [
                                  ...localProduct.activityPatternDefinitions,
                                ];
                                newActivityPatterns[apdIdx] = {
                                  ...newActivityPatterns[apdIdx],
                                  name: e.target.value,
                                };
                                handleChange(
                                  "activityPatternDefinitions",
                                  newActivityPatterns
                                );
                              }}
                            />
                          </FormControl>
                          <CodesetSelect
                            label="Type"
                            codesetCode="SLC007"
                            value={
                              apd.type
                                ? { code: apd.type, description: "" }
                                : null
                            }
                            onChange={(selected) => {
                              const newActivityPatterns = [
                                ...localProduct.activityPatternDefinitions,
                              ];
                              newActivityPatterns[apdIdx].type = selected.code;
                              handleChange(
                                "activityPatternDefinitions",
                                newActivityPatterns
                              );
                            }}
                            size="xs"
                          />
                        </SimpleGrid>

                        <Tabs
                          mt={2}
                          orientation="horizontal"
                          variant="solid-rounded"
                          colorScheme="linkedin">
                          <TabList>
                            {apd.stages?.map((stage, stageidx) => (
                              <Tab fontSize="xs" key={stage.id || stageidx}>
                                Stage {stage.number}
                              </Tab>
                            ))}
                            <IconButton
                              icon={<HiOutlinePlus />}
                              size="sm"
                              onClick={() => {
                                const newActivityPatterns = [
                                  ...localProduct.activityPatternDefinitions,
                                ];
                                if (!newActivityPatterns[apdIdx].stages) {
                                  newActivityPatterns[apdIdx].stages = [];
                                }
                                // Find the next available stage number
                                const usedNumbers = newActivityPatterns[
                                  apdIdx
                                ].stages.map((s) => s.number);
                                let nextNumber = 0;
                                while (usedNumbers.includes(nextNumber)) {
                                  nextNumber++;
                                }
                                newActivityPatterns[apdIdx].stages.push({
                                  id: crypto.randomUUID(),
                                  number: nextNumber,
                                  activities: [],
                                });
                                handleChange(
                                  "activityPatternDefinitions",
                                  newActivityPatterns
                                );
                              }}
                            />
                          </TabList>
                          <TabPanels>
                            {apd.stages?.map((stage, stageIdx) => (
                              <TabPanel key={stage.id || stageIdx}>
                                <Box
                                  borderWidth="1px"
                                  borderRadius="md"
                                  p={1}
                                  mb={1}
                                  borderColor={"gray.500"}>
                                  <SimpleGrid columns={2} spacing={4}>
                                    <FormControl>
                                      <FormLabel fontSize="xs">
                                        Stage#
                                      </FormLabel>
                                      <Input
                                        size="xs"
                                        value={stage.number}
                                        onChange={(e) => {
                                          const updated = [
                                            ...localProduct.activityPatternDefinitions,
                                          ];
                                          updated[apdIdx].stages[
                                            stageIdx
                                          ].number = e.target.value;
                                          handleChange(
                                            "activityPatternDefinitions",
                                            updated
                                          );
                                        }}
                                      />
                                    </FormControl>
                                    <FormControl>
                                      <FormLabel fontSize="xs">
                                        Status
                                      </FormLabel>
                                      <Input
                                        size="xs"
                                        value={stage.status}
                                        onChange={(e) => {
                                          const updated = [
                                            ...localProduct.activityPatternDefinitions,
                                          ];
                                          updated[apdIdx].stages[
                                            stageIdx
                                          ].status = e.target.value;
                                          handleChange(
                                            "activityPatternDefinitions",
                                            updated
                                          );
                                        }}
                                      />
                                    </FormControl>
                                  </SimpleGrid>

                                  <HStack justify="flex-end">
                                    <Tooltip label="Delete stage...">
                                      <IconButton
                                        icon={<HiOutlineTrash />}
                                        size="xs"
                                        onClick={() => {
                                          const newActivityPatterns = [
                                            ...localProduct.activityPatternDefinitions,
                                          ];
                                          newActivityPatterns[
                                            apdIdx
                                          ].stages.splice(stageIdx, 1);
                                          handleChange(
                                            "activityPatternDefinitions",
                                            newActivityPatterns
                                          );
                                        }}
                                      />
                                    </Tooltip>
                                  </HStack>
                                </Box>

                                <HStack>
                                  <Heading size="xs" mb={2}>
                                    Activities
                                  </Heading>
                                  <Tooltip label="Add activity">
                                    <IconButton
                                      icon={<HiOutlinePlus />}
                                      aria-label="Add activity"
                                      size="md"
                                      onClick={() => {
                                        const updated = [
                                          ...localProduct.activityPatternDefinitions,
                                        ];
                                        if (
                                          !updated[apdIdx].stages[stageIdx]
                                            .activities
                                        ) {
                                          updated[apdIdx].stages[
                                            stageIdx
                                          ].activities = [];
                                        }
                                        updated[apdIdx].stages[
                                          stageIdx
                                        ].activities.push({
                                          id: crypto.randomUUID(),
                                          name: "New Activity",
                                          type: "",
                                          oracles: [],
                                        });
                                        handleChange(
                                          "activityPatternDefinitions",
                                          updated
                                        );
                                      }}
                                    />
                                  </Tooltip>
                                </HStack>
                                {stage.activities?.map(
                                  (activity, activityIdx) => (
                                    <Box
                                      key={activityIdx}
                                      bg={cardBg}
                                      p={2}
                                      rounded="lg"
                                      shadow="md"
                                      border="1px solid"
                                      borderColor="gray.500"
                                      minW="200px"
                                      maxW="fit-content"
                                      transition="all 0.2s ease"
                                      _hover={{
                                        shadow: "lg",
                                        transform: "translateY(-4px)",
                                      }}>
                                      <HStack justify="space-between">
                                        <FormControl>
                                          <FormLabel fontSize="xs">
                                            Name
                                          </FormLabel>
                                          <Input
                                            size="xs"
                                            value={activity.name}
                                            onChange={(e) => {
                                              const updated = [
                                                ...localProduct.activityPatternDefinitions,
                                              ];
                                              updated[apdIdx].stages[
                                                stageIdx
                                              ].activities[activityIdx].name =
                                                e.target.value;
                                              handleChange(
                                                "activityPatternDefinitions",
                                                updated
                                              );
                                            }}
                                          />
                                        </FormControl>

                                        <FormControl>
                                          <FormLabel fontSize="xs">
                                            Type
                                          </FormLabel>
                                          <Input
                                            size="xs"
                                            value={activity.type}
                                            onChange={(e) => {
                                              const updated = [
                                                ...localProduct.activityPatternDefinitions,
                                              ];
                                              updated[apdIdx].stages[
                                                stageIdx
                                              ].activities[activityIdx].type =
                                                e.target.value;
                                              handleChange(
                                                "activityPatternDefinitions",
                                                updated
                                              );
                                            }}
                                          />
                                        </FormControl>
                                      </HStack>

                                      <Box
                                        borderWidth="1px"
                                        borderRadius="md"
                                        mt={2}
                                        p={1}
                                        mb={1}
                                        borderColor="gray.500">
                                        <HStack spacing={4} mb={2}>
                                          <Heading size="xs" mb={2}>
                                            Oracles
                                          </Heading>
                                          <Tooltip
                                            label="Add Oracle..."
                                            aria-label="Add Oracle tooltip">
                                            <IconButton
                                              icon={<HiOutlinePlus />}
                                              size="sm"
                                              onClick={() => {
                                                const updated = [
                                                  ...localProduct.activityPatternDefinitions,
                                                ];
                                                updated[apdIdx].stages[
                                                  stageIdx
                                                ].activities[
                                                  activityIdx
                                                ].oracles.push({
                                                  id: crypto.randomUUID(),
                                                  participantFunctionCode: null,
                                                  party: null,
                                                });
                                                handleChange(
                                                  "activityPatternDefinitions",
                                                  updated
                                                );
                                              }}
                                            />
                                          </Tooltip>
                                        </HStack>

                                        <Table
                                          variant="unstyled"
                                          size="sm"
                                          fontSize="xs"
                                          style={{
                                            tableLayout: "auto",
                                            width: "100%",
                                          }}
                                          sx={{
                                            "tbody tr:nth-of-type(odd)": {
                                              backgroundColor: "gray.200",
                                            },
                                          }}>
                                          <Thead>
                                            <Tr>
                                              <Th fontSize="xs">Role</Th>
                                              <Th fontSize="xs">
                                                Participant Function
                                              </Th>
                                              <Th fontSize="xs">Party</Th>
                                              <Th fontSize="xs"></Th>
                                            </Tr>
                                          </Thead>

                                          <Tbody>
                                            {(activity.oracles || []).map(
                                              (oracle, oracleIdx) => (
                                                <Tr key={oracle.id}>
                                                  <Td
                                                    style={{ padding: "8px" }}>
                                                    <CodesetSelect
                                                      size="xs"
                                                      codesetCode="B06"
                                                      value={
                                                        oracle.participantFunctionCode
                                                          ? {
                                                              code: oracle.participantFunctionCode,
                                                              description: "",
                                                            }
                                                          : null
                                                      }
                                                      onChange={(selected) => {
                                                        updateOracleField(
                                                          apdIdx,
                                                          stageIdx,
                                                          activityIdx,
                                                          oracleIdx,
                                                          "participantFunctionCode",
                                                          selected
                                                            ? selected.code
                                                            : null
                                                        );
                                                      }}
                                                    />
                                                  </Td>
                                                  <Td
                                                    style={{ padding: "8px" }}>
                                                    <CodesetSelect
                                                      size="xs"
                                                      codesetCode="3035"
                                                      value={
                                                        oracle.roleCode
                                                          ? {
                                                              code: oracle.roleCode,
                                                              description: "",
                                                            }
                                                          : null
                                                      }
                                                      onChange={(selected) => {
                                                        updateOracleField(
                                                          apdIdx,
                                                          stageIdx,
                                                          activityIdx,
                                                          oracleIdx,
                                                          "roleCode",
                                                          selected
                                                            ? selected.code
                                                            : null
                                                        );
                                                      }}
                                                    />
                                                  </Td>
                                                  <Td>
                                                    <PartySelectById
                                                      size="xs"
                                                      label=""
                                                      selectedPartyId={
                                                        oracle.party ?? ""
                                                      }
                                                      onChange={(id) => {
                                                        updateOracleField(
                                                          apdIdx,
                                                          stageIdx,
                                                          activityIdx,
                                                          oracleIdx,
                                                          "party",
                                                          id || null
                                                        );
                                                      }}
                                                    />
                                                  </Td>

                                                  <Td>
                                                    <Tooltip label="Delete Oracle">
                                                      <IconButton
                                                        icon={
                                                          <HiOutlineTrash />
                                                        }
                                                        size="xs"
                                                        onClick={() => {
                                                          const updated = [
                                                            ...localProduct.activityPatternDefinitions,
                                                          ];
                                                          updated[
                                                            apdIdx
                                                          ].stages[
                                                            stageIdx
                                                          ].activities[
                                                            activityIdx
                                                          ].oracles.splice(
                                                            oracleIdx,
                                                            1
                                                          );
                                                          handleChange(
                                                            "activityPatternDefinitions",
                                                            updated
                                                          );
                                                        }}
                                                      />
                                                    </Tooltip>
                                                  </Td>
                                                </Tr>
                                              )
                                            )}
                                          </Tbody>
                                        </Table>
                                      </Box>

                                      <HStack justify="flex-end">
                                        <Tooltip label="Delete activity...">
                                          <IconButton
                                            icon={<HiOutlineTrash />}
                                            size="xs"
                                            onClick={() => {
                                              const updated = [
                                                ...localProduct.activityPatternDefinitions,
                                              ];
                                              updated[apdIdx].stages[
                                                stageIdx
                                              ].activities.splice(
                                                activityIdx,
                                                1
                                              );
                                              handleChange(
                                                "activityPatternDefinitions",
                                                updated
                                              );
                                            }}
                                          />
                                        </Tooltip>
                                      </HStack>
                                    </Box>
                                  )
                                )}
                              </TabPanel>
                            ))}
                          </TabPanels>
                        </Tabs>

                        <HStack justify="flex-end">
                          <Tooltip label="Delete activity pattern...">
                            <IconButton
                              icon={<HiOutlineTrash />}
                              size="xs"
                              onClick={() => {
                                const newActivityPatterns = [
                                  ...localProduct.activityPatternDefinitions,
                                ];
                                newActivityPatterns.splice(apdIdx, 1);
                                handleChange(
                                  "activityPatternDefinitions",
                                  newActivityPatterns
                                );
                              }}
                            />
                          </Tooltip>
                        </HStack>
                      </Box>
                    )
                  )}
                </TabPanel>

                <TabPanel>
                  <HStack spacing={4} mb={2}>
                    <Heading size="sm" mb={2}>
                      Product Controls
                    </Heading>
                    <Tooltip
                      label="Add Product Control entry..."
                      aria-label="Add Product Control entry tooltip">
                      <IconButton
                        icon={<HiOutlinePlus />}
                        size="sm"
                        onClick={() => {
                          const updated = [...(localProduct.controls || [])];
                          updated.push({
                            type: "",
                            route: "",
                            objectType: "",
                            objectId: null,
                          });
                          handleChange("controls", updated);
                        }}
                      />
                    </Tooltip>
                  </HStack>

                  <Table
                    variant="unstyled"
                    size="sm"
                    fontSize="xs"
                    style={{ tableLayout: "auto", width: "100%" }}
                    sx={{
                      "tbody tr:nth-of-type(odd)": {
                        backgroundColor: "gray.200",
                      },
                    }}>
                    <Thead>
                      <Tr>
                        <Th fontSize="xs">Name</Th>
                        <Th fontSize="xs">Register Type</Th>
                        <Th fontSize="xs">Entry Type</Th>
                        <Th fontSize="xs">Signal Type</Th>
                        <Th></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {localProduct.controls?.map((control, controlidx) => (
                        <Tr key={control.name || controlidx}>
                          <Td>
                            <Input
                              size="xs"
                              value={control.name || ""}
                              onChange={(e) => {
                                const updated = [...localProduct.controls];
                                updated[controlidx].name = e.target.value;
                                handleChange("controls", updated);
                              }}
                            />
                          </Td>

                          {registerLoading ? (
                            <Td colSpan={2}>Loading registers...</Td>
                          ) : (
                            <>
                              <Td>
                                <Select
                                  placeholder="Select Register"
                                  value={control.registerType || ""}
                                  onChange={(e) => {
                                    const updated = [...localProduct.controls];
                                    updated[controlidx].registerType =
                                      e.target.value;
                                    updated[controlidx].entryType = ""; // reset entry on register change
                                    handleChange("controls", updated);
                                  }}
                                  size="xs">
                                  {registers.map((reg) => (
                                    <option key={reg._id} value={reg._id}>
                                      {reg.name}
                                    </option>
                                  ))}
                                </Select>
                              </Td>

                              <Td>
                                <Select
                                  placeholder="Select Register Entry"
                                  value={control.entryType || ""}
                                  onChange={(e) => {
                                    const updated = [...localProduct.controls];
                                    updated[controlidx].entryType =
                                      e.target.value;
                                    handleChange("controls", updated);
                                  }}
                                  size="xs"
                                  isDisabled={!control.registerType}>
                                  {control.registerType &&
                                    registers
                                      .find(
                                        (reg) =>
                                          reg._id === control.registerType
                                      )
                                      ?.entries.map((entry) => (
                                        <option key={entry.id} value={entry.id}>
                                          {entry.name}{" "}
                                          {/* shown to user, but not stored */}
                                        </option>
                                      ))}
                                </Select>
                              </Td>
                            </>
                          )}

                          <Td>
                            <CodesetSelect
                              codesetCode="SLC008"
                              value={
                                control.signal
                                  ? {
                                      code: control.signal,
                                      description: "",
                                    }
                                  : null
                              }
                              onChange={(selected) => {
                                const updated = [...localProduct.controls];
                                updated[controlidx].signal = selected.code;
                                handleChange("controls", updated);
                              }}
                              size="xs"
                            />
                          </Td>
                          <Td>
                            <Tooltip label="Delete row">
                              <IconButton
                                icon={<HiOutlineTrash />}
                                size="xs"
                                onClick={() => {
                                  const updated = [...localProduct.controls];
                                  updated.splice(controlidx, 1);
                                  handleChange("controls", updated);
                                }}
                              />
                            </Tooltip>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductDefinitionEditor;
